import { useCallback, useEffect, useState } from 'react';
import { Search, Plus, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { invInput, invField, money, StockBadge, errMsg } from './invApi';
import { ItemDialog } from './ItemDialog';

const NewItemForm = ({ api, onCreated }) => {
  const [f, setF] = useState({ name: '', part_number: '' });
  const save = async () => {
    try { const it = await api.post('/items', f); setF({ name: '', part_number: '' }); onCreated(it); toast.success('Pièce ajoutée'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2 bg-slate-900 border border-slate-800 rounded-xl p-3" data-testid="new-item-form">
      <input className={`${invInput} flex-1 min-w-[180px]`} placeholder="Nom de la pièce" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="new-item-name" />
      <input className={`${invField} w-40`} placeholder="No pièce" value={f.part_number} onChange={e => setF({ ...f, part_number: e.target.value })} data-testid="new-item-pn" />
      <button onClick={save} disabled={!f.name.trim()} className="px-4 rounded-lg bg-amber-500 text-black text-sm font-semibold disabled:opacity-40" data-testid="new-item-save">Ajouter</button>
    </div>
  );
};

export const ItemsTab = ({ api, canSeeCosts, refreshKey }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => api.get('/items', { q }).then(setItems).catch(() => toast.error('Inventaire indisponible')), [api, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, refreshKey]);

  return (
    <div className="space-y-3" data-testid="inventory-items-tab">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-2.5 text-slate-500" />
          <input className={`${invInput} pl-9`} placeholder="Chercher : LF9009, filtre à huile, Fleetguard…" value={q} onChange={e => setQ(e.target.value)} data-testid="inventory-search" />
        </div>
        {canSeeCosts && <button onClick={() => setAdding(!adding)} className="px-3 rounded-lg border border-slate-700 text-slate-300 hover:text-white" data-testid="inventory-add-toggle"><Plus size={16} /></button>}
      </div>
      {adding && <NewItemForm api={api} onCreated={(it) => { setAdding(false); load(); setOpen(it.id); }} />}
      {!items.length && <p className="text-sm text-slate-500 py-8 text-center" data-testid="inventory-empty">Aucune pièce. Recevez une facture fournisseur : l'inventaire se construit tout seul.</p>}
      <div className="divide-y divide-slate-800 bg-slate-900 border border-slate-800 rounded-xl">
        {items.map(it => (
          <button key={it.id} onClick={() => setOpen(it.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-800/50 transition-colors" data-testid={`inventory-item-${it.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{it.name}</p>
              <p className="text-[11px] text-slate-500 truncate">{it.part_number || '—'} · {it.category_name}</p>
            </div>
            {it.location_label && <span className="hidden sm:flex items-center gap-1 text-xs text-amber-300 font-mono"><MapPin size={12} />{it.location_label}</span>}
            {canSeeCosts && <span className="hidden md:block text-xs text-slate-400 w-20 text-right">{money(it.sale_price)}</span>}
            <StockBadge item={it} />
          </button>
        ))}
      </div>
      {open && <ItemDialog api={api} itemId={open} canSeeCosts={canSeeCosts} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
};
